import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { HeartPulse, RefreshCw, AlertTriangle, History, Activity } from "lucide-react";


const COMPONENT_LABELS = {
  db: "Database",
  scheduler: "Background jobs",
  stripe: "Payments",
  email: "Email delivery",
  slack: "Slack alerts",
};

const COMPONENT_DESCRIPTIONS = {
  db: "Stores accounts, projects, audits and content drafts.",
  scheduler: "Runs daily audits, scheduled posts, the weekly digest and ops alerts.",
  stripe: "Subscription billing and self-serve plan management.",
  email: "Transactional email — password resets, verifications, digests.",
  slack: "Optional ops alert routing for the operator team.",
};

const OK_STATES = new Set(["ok", "running", "configured"]);


function pretty(status) {
  return ({
    ok: "Operational",
    running: "Operational",
    configured: "Operational",
    not_configured: "Not configured",
    stopped: "Stopped",
    down: "Down",
  })[status] || status;
}


function fmtDuration(mins) {
  if (mins === null || mins === undefined) return "—";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}


function fmtIncidentDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
}


export default function StatusPage() {
  const [data, setData] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const load = async () => {
    setBusy(true);
    try {
      const [{ data: h }, { data: inc }] = await Promise.all([
        api.get("/health/full"),
        api.get("/health/incidents?limit=10"),
      ]);
      setData(h);
      setIncidents(inc || []);
      setError(null);
      setLastChecked(new Date());
    } catch (e) {
      setError("Unable to reach our health endpoint right now.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh every 30s.
    timerRef.current = setInterval(load, 30_000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const overallOk = data?.status === "ok" && !error;
  const headline = error
    ? "Health check unavailable"
    : overallOk
      ? "All systems operational."
      : "Some systems are degraded.";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-foreground/15">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="status-brand-home">
            <div className="bg-foreground text-background font-semibold text-base px-2 py-1 tracking-tight">S</div>
            <span className="font-semibold tracking-tight text-lg">ARLOSEO</span>
          </Link>
          <Link to="/" className="font-mono-data text-[11px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground transition-colors">
            ← Back to arloseo.app
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
        {/* Heading */}
        <div className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-muted-foreground">// system status</div>
        <h1
          data-testid="status-headline"
          className={`font-semibold tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-3 ${
            error ? "text-rose-700" : overallOk ? "text-foreground" : "text-amber-700"
          }`}
        >
          {headline}
        </h1>

        {/* Status pill */}
        <div className="mt-8 flex items-center gap-3 flex-wrap" data-testid="status-pill">
          <div className={`inline-flex items-center gap-2 border px-3 py-1.5 ${
            overallOk
              ? "border-emerald-500/40 bg-emerald-50 text-emerald-700"
              : "border-amber-500/40 bg-amber-50 text-amber-800"
          }`}>
            <HeartPulse className="w-4 h-4" strokeWidth={2.5} />
            <span className="font-mono-data text-[11px] uppercase tracking-[0.18em] font-semibold">
              {overallOk ? "all green" : "degraded"}
            </span>
          </div>
          <div className="font-mono-data text-[11px] uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-1.5">
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
            {lastChecked ? `last checked ${lastChecked.toLocaleTimeString("en-US", { hour12: true })}` : "checking…"}
            <span className="mx-1 opacity-50">·</span>
            auto-refreshes every 30s
          </div>
        </div>

        {error && (
          <div
            className="mt-10 border border-rose-500/40 bg-rose-50 p-5 flex items-start gap-3"
            data-testid="status-error"
          >
            <AlertTriangle className="w-5 h-5 text-rose-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-rose-900">Health check unavailable.</div>
              <p className="text-sm text-rose-800 mt-1 max-w-prose">
                {error} If this persists, our team is likely already aware. Email{" "}
                <a className="underline" href="mailto:hello@arloseo.app">hello@arloseo.app</a> for status.
              </p>
            </div>
          </div>
        )}

        {/* Components */}
        {data && (
          <div className="mt-12 grid sm:grid-cols-2 gap-3" data-testid="status-components">
            {Object.entries(data.components || {}).map(([name, c]) => {
              const ok = OK_STATES.has(c.status);
              const dim = c.status === "not_configured";
              return (
                <div
                  key={name}
                  className={`border p-5 ${
                    ok ? "border-foreground/15 bg-white" :
                    dim ? "border-foreground/10 bg-zinc-50" :
                    "border-rose-500/40 bg-rose-50"
                  }`}
                  data-testid={`status-component-${name}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <span className={`inline-block w-2.5 h-2.5 rounded-full ${
                        ok ? "bg-emerald-500" :
                        dim ? "bg-zinc-300" :
                        "bg-rose-500"
                      }`} />
                      <div className="font-semibold text-base">{COMPONENT_LABELS[name] || name}</div>
                    </div>
                    <div className={`font-mono-data text-[10px] uppercase tracking-[0.16em] font-semibold ${
                      ok ? "text-emerald-700" :
                      dim ? "text-muted-foreground" :
                      "text-rose-700"
                    }`}>
                      {pretty(c.status)}{c.mode ? ` · ${c.mode}` : ""}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 leading-snug">
                    {COMPONENT_DESCRIPTIONS[name] || ""}
                  </p>
                  {c.reason && (
                    <div className="font-mono-data text-[10px] uppercase tracking-widest text-rose-700 mt-2">
                      ↳ {c.reason}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Recent incidents */}
        <div className="mt-14" data-testid="status-incidents">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-4 h-4" />
            <div className="font-mono-data text-[11px] uppercase tracking-[0.18em]">Recent incidents</div>
          </div>
          {incidents.length === 0 ? (
            <div className="border border-dashed border-foreground/15 p-5 text-sm text-muted-foreground"
                 data-testid="status-incidents-empty">
              No incidents recorded. <span className="opacity-70">When an automated alert fires (elevated p95 latency or error rate),
              it shows up here with start time, duration and resolution.</span>
            </div>
          ) : (
            <ul className="border border-foreground/15 divide-y divide-dashed divide-foreground/15">
              {incidents.map((inc) => {
                const ongoing = inc.status === "ongoing";
                return (
                  <li key={inc.id} className="px-5 py-3.5 flex items-center gap-4 flex-wrap"
                      data-testid={`status-incident-${inc.id}`}>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className={`inline-block w-2 h-2 rounded-full ${
                        ongoing ? "bg-amber-500 animate-pulse" : "bg-emerald-500"
                      }`} />
                      <div className="font-semibold text-sm truncate">{inc.label}</div>
                      {ongoing && (
                        <span className="font-mono-data text-[10px] uppercase tracking-[0.16em] font-semibold bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5">
                          ongoing
                        </span>
                      )}
                    </div>
                    <div className="font-mono-data text-[11px] text-muted-foreground">
                      <span className="uppercase tracking-widest">started</span>{" "}
                      <span className="text-foreground">{fmtIncidentDate(inc.started_at)}</span>
                    </div>
                    <div className="font-mono-data text-[11px] text-muted-foreground">
                      <span className="uppercase tracking-widest">duration</span>{" "}
                      <span className={ongoing ? "text-amber-700 font-semibold" : "text-foreground"}>
                        {fmtDuration(inc.duration_minutes)}{ongoing && "+"}
                      </span>
                    </div>
                    {inc.peak_observed && (
                      <div className="font-mono-data text-[11px] text-muted-foreground">
                        <span className="uppercase tracking-widest">peak</span>{" "}
                        <span className="text-foreground">{inc.peak_observed}</span>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer note */}
        <div className="mt-16 pt-8 border-t border-dashed border-foreground/15 font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
          Powered by <a href="/api/health/full" className="underline">/api/health/full</a>.
          Operator alerts route to email and (when configured) Slack with a 60-minute cooldown per condition.
        </div>
      </main>
    </div>
  );
}
